import { ref } from 'vue'
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  getDoc,
  runTransaction,
  addDoc
} from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export function usePointChargeAdmin() {
  const chargeRequests = ref([])
  const loading = ref(false)
  const error = ref(null)

  // 전체 충전 신청 목록 조회
  const loadAllChargeRequests = async (statusFilter = 'all') => {
    try {
      loading.value = true
      let q

      if (statusFilter === 'all') {
        q = query(
          collection(db, 'pointChargeRequests'),
          orderBy('createdAt', 'desc')
        )
      } else {
        q = query(
          collection(db, 'pointChargeRequests'),
          where('status', '==', statusFilter),
          orderBy('createdAt', 'desc')
        )
      }

      const snapshot = await getDocs(q)
      chargeRequests.value = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
    } catch (err) {
      console.error('충전 신청 목록 로드 실패:', err)
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  // 대기 중인 신청 수 조회
  const getPendingCount = async () => {
    try {
      const q = query(
        collection(db, 'pointChargeRequests'),
        where('status', '==', 'pending')
      )
      const snapshot = await getDocs(q)
      return snapshot.size
    } catch (err) {
      console.error('대기 수 조회 실패:', err)
      return 0
    }
  }

  // 충전 승인 (포인트 적립)
  const approveChargeRequest = async (requestId) => {
    try {
      const requestRef = doc(db, 'pointChargeRequests', requestId)
      const requestDoc = await getDoc(requestRef)

      if (!requestDoc.exists()) {
        return { success: false, error: '신청 내역을 찾을 수 없습니다.' }
      }

      const requestData = requestDoc.data()

      if (requestData.status !== 'pending') {
        return { success: false, error: '대기 중인 신청만 승인할 수 있습니다.' }
      }

      const partnerId = requestData.partnerId
      const totalAmount = requestData.totalAmount

      // 트랜잭션으로 포인트 적립 + 신청 상태 업데이트
      const result = await runTransaction(db, async (transaction) => {
        // 파트너 포인트 문서 가져오기
        const pointRef = doc(db, 'partnerPoints', partnerId)
        const pointDoc = await transaction.get(pointRef)

        const currentData = pointDoc.exists() ? pointDoc.data() : {
          balance: 0,
          totalCharged: 0,
          totalUsed: 0
        }

        const newBalance = currentData.balance + totalAmount
        const newTotalCharged = currentData.totalCharged + totalAmount

        // 포인트 잔액 업데이트
        transaction.set(pointRef, {
          ...currentData,
          balance: newBalance,
          totalCharged: newTotalCharged,
          updatedAt: new Date().toISOString()
        }, { merge: true })

        // 충전 신청 상태 업데이트
        transaction.update(requestRef, {
          status: 'approved',
          approvedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })

        return { newBalance }
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        partnerId: partnerId,
        type: 'charge',
        amount: totalAmount,
        balanceAfter: result.newBalance,
        description: `파트너스 포인트 충전 ${totalAmount.toLocaleString()}P`,
        relatedId: requestId,
        createdAt: new Date().toISOString()
      })

      return { success: true, balance: result.newBalance }
    } catch (err) {
      console.error('충전 승인 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 충전 거절
  const rejectChargeRequest = async (requestId, reason = '') => {
    try {
      const requestRef = doc(db, 'pointChargeRequests', requestId)
      const requestDoc = await getDoc(requestRef)

      if (!requestDoc.exists()) {
        return { success: false, error: '신청 내역을 찾을 수 없습니다.' }
      }

      if (requestDoc.data().status !== 'pending') {
        return { success: false, error: '대기 중인 신청만 거절할 수 있습니다.' }
      }

      await updateDoc(requestRef, {
        status: 'rejected',
        rejectReason: reason,
        rejectedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })

      return { success: true }
    } catch (err) {
      console.error('충전 거절 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 어드민 직접 포인트 지급
  const givePointToPartner = async (partnerId, amount, description = '') => {
    try {
      if (!partnerId || !amount || amount <= 0) {
        return { success: false, error: '유효하지 않은 파라미터입니다.' }
      }

      const result = await runTransaction(db, async (transaction) => {
        // 파트너 포인트 문서 가져오기
        const pointRef = doc(db, 'partnerPoints', partnerId)
        const pointDoc = await transaction.get(pointRef)

        const currentData = pointDoc.exists() ? pointDoc.data() : {
          balance: 0,
          totalCharged: 0,
          totalUsed: 0,
          createdAt: new Date().toISOString()
        }

        const newBalance = currentData.balance + amount
        const newTotalCharged = currentData.totalCharged + amount

        // 포인트 잔액 업데이트
        transaction.set(pointRef, {
          ...currentData,
          balance: newBalance,
          totalCharged: newTotalCharged,
          updatedAt: new Date().toISOString()
        }, { merge: true })

        return { newBalance }
      })

      // 거래 내역 추가
      await addDoc(collection(db, 'pointTransactions'), {
        partnerId: partnerId,
        type: 'admin_give',
        amount: amount,
        balanceAfter: result.newBalance,
        description: description || `관리자 포인트 지급 ${amount.toLocaleString()}P`,
        createdAt: new Date().toISOString()
      })

      return { success: true, balance: result.newBalance }
    } catch (err) {
      console.error('포인트 지급 실패:', err)
      return { success: false, error: err.message }
    }
  }

  // 파트너 목록 조회 (포인트 지급용)
  const getPartnerList = async () => {
    try {
      const q = query(
        collection(db, 'users'),
        where('role', '==', 'partner')
      )
      const snapshot = await getDocs(q)
      const partners = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))

      // 각 파트너의 영업명(displayName) 가져오기
      for (const partner of partners) {
        try {
          const ppQuery = query(
            collection(db, 'propertyPartners'),
            where('partnerId', '==', partner.id),
            where('status', '==', 'active')
          )
          const ppSnapshot = await getDocs(ppQuery)
          if (!ppSnapshot.empty) {
            const ppData = ppSnapshot.docs[0].data()
            partner.displayName = ppData.displayName || partner.name
            partner.propertyName = ppData.propertyName
          }
        } catch {
          // 영업명 조회 실패 시 기본 이름 사용
        }
      }

      return partners
    } catch (err) {
      console.error('파트너 목록 조회 실패:', err)
      return []
    }
  }

  // 특정 파트너 포인트 조회
  const getPartnerPoint = async (partnerId) => {
    try {
      const pointDoc = await getDoc(doc(db, 'partnerPoints', partnerId))
      if (pointDoc.exists()) {
        return pointDoc.data()
      }
      return { balance: 0, totalCharged: 0, totalUsed: 0 }
    } catch (err) {
      console.error('파트너 포인트 조회 실패:', err)
      return null
    }
  }

  return {
    chargeRequests,
    loading,
    error,
    loadAllChargeRequests,
    getPendingCount,
    approveChargeRequest,
    rejectChargeRequest,
    givePointToPartner,
    getPartnerList,
    getPartnerPoint
  }
}
